/**
 * Pantry API Service
 *
 * With Better Auth, session cookies are sent automatically via credentials: 'include'.
 */

import { apiFetch } from "./client";
import type {
  PantryItem,
  PantryListItem,
  PantryExpiringItem,
  PantryStats,
  PantryEventOutcome,
  AddPantryItemRequest,
  UpdatePantryItemRequest,
  BulkAddPantryRequest,
  Ingredient,
} from "./types";

/**
 * Get all pantry items for the current user
 */
export async function getPantryItems(): Promise<PantryListItem[]> {
  return apiFetch<PantryListItem[]>("/pantry");
}

/**
 * Get household waste/reuse stats for the "This month" rail card (issue
 * #377, B5). `period` is currently always "month" -- see resolvePeriodStart
 * in apps/api/src/services/pantry-waste-stats.ts.
 */
export async function getPantryStats(period: "month" = "month"): Promise<PantryStats> {
  return apiFetch<PantryStats>(`/pantry/stats?period=${period}`);
}

/**
 * Get household pantry items expiring soon (see CAUTION_DAYS_LEFT,
 * apps/api/src/services/pantry-freshness.ts), ascending by days left.
 */
export async function getExpiringPantryItems(): Promise<PantryExpiringItem[]> {
  return apiFetch<PantryExpiringItem[]>("/pantry/expiring");
}

/**
 * Get household pantry items running low (documented v1 heuristic --
 * see isLowStock in apps/api/src/services/pantry-freshness.ts).
 */
export async function getLowStockPantryItems(): Promise<PantryItem[]> {
  return apiFetch<PantryItem[]>("/pantry/low-stock");
}

/**
 * Add a single item to the pantry
 */
export async function addPantryItem(
  data: AddPantryItemRequest,
): Promise<PantryItem> {
  return apiFetch<PantryItem>("/pantry", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/**
 * Add multiple items to the pantry at once
 */
export async function bulkAddPantryItems(
  data: BulkAddPantryRequest,
): Promise<PantryItem[]> {
  return apiFetch<PantryItem[]>("/pantry/bulk", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

/**
 * Update a pantry item
 */
export async function updatePantryItem(
  id: string,
  data: UpdatePantryItemRequest,
): Promise<PantryItem> {
  return apiFetch<PantryItem>(`/pantry/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

/**
 * Remove a single item from the pantry. `outcome` is optional -- omit it to
 * let the server auto-detect from the active-plan map (silently records
 * `consumed` when there's evidence, otherwise falls back to the documented
 * no-outcome-DELETE default). Pass it explicitly when the caller already
 * knows the answer (the 3-option outcome prompt, PantryOutcomeDialog.tsx).
 */
export async function removePantryItem(
  id: string,
  outcome?: PantryEventOutcome,
): Promise<void> {
  return apiFetch<void>(`/pantry/${id}`, {
    method: "DELETE",
    body: JSON.stringify(outcome ? { outcome } : {}),
  });
}

/**
 * Clear all items from the pantry
 */
export async function clearPantry(): Promise<void> {
  return apiFetch<void>("/pantry", {
    method: "DELETE",
  });
}

/**
 * Accept a batch of "you might already have" staple guesses, seeding the
 * pantry from grocery-list lines the server flagged with `stapleGuess`
 * (#388). `skipped` is not an error -- see POST /pantry/from-staple-guess
 * in apps/api/src/routes/pantry.ts for why an ingredient can land there.
 */
export async function acceptStapleGuesses(
  ingredientIds: string[],
): Promise<{ created: PantryItem[]; skipped: string[] }> {
  return apiFetch("/pantry/from-staple-guess", {
    method: "POST",
    body: JSON.stringify({ ingredientIds }),
  });
}

/**
 * Get ingredients grouped by category (for quick-select UI)
 */
export async function getIngredientsByCategory(): Promise<
  Record<string, Ingredient[]>
> {
  return apiFetch<Record<string, Ingredient[]>>("/ingredients/by-category");
}
